import React, {
  useCallback, useEffect, useMemo, useRef, useState, Fragment, RefObject, ReactElement,
} from 'react';
import { useDispatch } from 'react-redux';
import Card from '../../../components/card/card.component';
import Table from '../../../components/table/table.component';
import TableHead from '../../../components/table-head/table-head.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import ExpansionGroup from '../../../components/expansion-group/expansion-group.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import Tooltip from '../../../components/tooltip/tooltip.component';
import ContextMenu from '../../../components/context-menu/context-menu.component';
import Checkbox from '../../../components/checkbox/checkbox.component';
import StatusBar from '../../../components/status-bar/status-bar.component';
import Loader from '../../../components/loader/loader.component';
import LongClickListItem from '../../../components/long-click-list-item/long-click-list-item.component';
import DraggableItem from '../../../components/draggable-item/draggable-item.component';
import DragHandle from '../../../components/drag-handle/drag-handle.component';
import FilteredLink from '../../../components/filtered-link/filtered-link.component';
import NoContentBlock from '../../../components/no-content-block/no-content-block.component';
import ActionButton from '../../../components/action-button/action-button.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import PaddedFlexContainer from '../../../components/padded-flex-container/padded-flex-container.component';
import HiddenBlock from '../../../components/hidden-block/hidden-block.component';
import List from '../../../components/list/list.component';
import ListItem from '../../../components/list-item/list-item.component';
import ListItemText from '../../../components/list-item-text/list-item-text.component';
import ListItemCheckbox from '../../../components/list-item-checkbox/list-item-checkbox.component';
import FieldUpload from '../../../components/field-upload/field-upload.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import IconTextBlock from '../../../components/icon-text-block/icon-text-block.component';
import ActionButtonContainer from '../../../components/action-button-container/action-button-container.component';
import ActionButtonNewItem from '../../../components/action-button-new-item/action-button-new-item.component';
import TableRowAddNewItem from '../../../components/table-row-add-new-item/table-row-add-new-item.component';
import TableRowNoContent from '../../../components/table-row-no-content/table-row-no-content.component';
import TableRowGroupName from '../../../components/table-row-group-name/table-row-group-name.component';
import TableCellCheckbox from '../../../components/table-cell-checkbox/table-cell-checkbox.component';
import ContentListContainerOffset
  from '../../app/common/content-list-container-offset/content-list-container-offset.component';
import DealFileRowForm from './deal-file-row-form/deal-file-row-form.component';
import DealFileRow from './deal-file-row/deal-file-row.component';
import EditIcon from '../../../icons/edit-icon';
import ExpandIcon from '../../../icons/expand-icon';
import CollapseIcon from '../../../icons/collapse-icon';
import DeleteItemIcon from '../../../icons/delete-item-icon';
import MoreIcon from '../../../icons/more-icon';
import RemoveIcon from '../../../icons/remove-icon';
import ArrowLinkIcon from '../../../icons/arrow-link-icon';
import DownloadIcon from '../../../icons/download-icon';
import AnalyzeIcon from '../../../icons/analyze-icon';
import ChatAssistantIcon from '../../../icons/chat-assistant-icon';
import FormatService from '../../../services/format-service';
import StructureService from '../../../services/structure-service';
import ScreenService from '../../../services/screen-service';
import SortService from '../../../services/sort-service';
import FormService from '../../../services/form-service';
import FileService from '../../../services/file-service';
import useScroll from '../../../hooks/use-scroll';
import useArrowNavigation from '../../../hooks/use-arrow-navigation';
import useScreenSmall from '../../../hooks/use-screen-small';
import useScreenMedium from '../../../hooks/use-screen-medium';
import useMobileDevice from '../../../hooks/use-mobile-device';
import useCurrentUserPermissions from '../../../hooks/use-current-user-permissions';
import { saveDealFileGroupOrderSettings } from './deal-files.action';
import {
  DATA_IS_ANALYZABLE, DATA_DEAL_FILE_GROUP_ID, DATA_DEAL_FILE_ID,
} from '../../../constants/system/data-attributes';
import {
  DEAL_FILE_CREATE, DEAL_FILE_DELETE, DEAL_FILE_GROUP_DELETE, DEAL_FILE_EDIT, DEAL_FILE_GROUP_EDIT, DEAL_FILE_LIST_VIEW,
  DEAL_FILE_TEXT_DATA_VIEW,
} from '../../../constants/entity/permissions';
import { FILE_EXTENSION_LIST_ANALYZABLE } from '../../../constants/system/file-extensions';
import { SORT_TYPE_ASC } from '../../../constants/system/sort-types';

const DealFiles: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const COLUMNS_TOTAL: number = 5;
  const COLUMNS_TOTAL_SIMPLE: number = 3;
  const COLUMNS_TOTAL_EDIT_MODE_OFFSET: number = 1;
  const COLUMNS_TOTAL_NAME: number = 2;
  const ID_PREFIX_CHECKBOX_EXPORT_GROUP: string = 'checkbox-export-group';

  const {
    isSaving,
    isSaveDisabled,
    isSelectMode,
    isExportMode,
    isEditMode,
    isCustomizeMode,
    isNewDeal,
    isSimple,
    isLoading,
    isDealFileAnalyzed,
    withAllRowsSelected,
    dealFilesData,
    dealFilesTotal,
    dealFileFiles,
    dealFileNewItems,
    dealFileNewGroupItems,
    dealFilesLoadedTotal,
    dealFileActiveIds,
    dealFileGroupActiveIds,
    dealFileInlineSaveId,
    dealFileGroupInlineSaveId,
    selectedRows,
    dealId,
    queryDealFileId,
    expandedGroups,
    accessLevelList,
    onInlineSave,
    onInlineCancel,
    onInlineEditModeButtonClick,
    onInlineGroupEditModeButtonClick,
    onOpenModal,
    onSelectRows,
    onSelectRow,
    onSelectAllGroupRows,
    onFileChange,
    onFilesChange,
    onFileDelete,
    onFileGroupsChange,
    onFileViewClick,
    onLongClick,
    onLoadBatchData,
    onAddNewItems,
    onDeleteClick,
    onDeleteGroupClick,
    onOpenDeleteConfirmModal,
    onOpenDeleteGroupConfirmModal,
    onOpenDealFileChatModal,
    onExpansionGroupChange,
    onDealFileAnalyzeClick,
  } = props;

  const GROUP_SORT_STATE: SortParams = useMemo((): SortParams => ({
    field: 'order',
    type: SORT_TYPE_ASC,
    isNumber: true,
  }), []);

  const dispatch: ReduxDispatch = useDispatch();
  const isSmallScreen: boolean = useScreenSmall();
  const isMediumScreen: boolean = useScreenMedium();
  const isMobileDevice: boolean = useMobileDevice();
  const permissionList: string[] = useCurrentUserPermissions();
  const tableRefs: RefObject<Record<string, HTMLTableElement>> = useRef({});
  const inputRefs: RefObject<Record<string, HTMLElement>> = useRef({});
  const containerRef: RefObject<HTMLDivElement> | null = useRef(null);
  const itemListRef: RefObject<HTMLDivElement> | null = useRef(null);

  const [menuAnchor, setMenuAnchor]: [HTMLElement | null, Function] = useState(null);
  const [groupMenuAnchor, setGroupMenuAnchor]: [HTMLElement | null, Function] = useState(null);
  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [isMessageError, setIsMessageError]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [dealFileGroups, setDealFileGroups]: [DealFileGroup[] | null, Function] = useState<StateValueDefault>(null);
  const [activeDealFileGroupIndex, setActiveDealFileGroupIndex]: [number | null, Function] = useState(null);
  const [hoverDealFileGroupIndex, setHoverDealFileGroupIndex]: [number | null, Function] = useState(null);

  const fileGroupDropDeps: DepsItem[] = useMemo((): DepsItem[] => ([
    dealFileNewItems, dealFileNewGroupItems, dealFileFiles, dealFilesLoadedTotal,
  ]), [dealFileNewItems, dealFileNewGroupItems, dealFileFiles, dealFilesLoadedTotal]);
  const fileDropDeps: DepsItem[][] = useMemo((): DepsItem[][] => [
    dealFileNewItems, dealFileFiles,
  ], [dealFileNewItems, dealFileFiles]);

  const commonDealFilesTotal: number = useMemo((): number => (
    dealFileGroups && dealFileGroups.length
      ? dealFileGroups.reduce((dealFileItemsTotal: number, dealFileGroupItem: DealFileGroup): number => (
        dealFileItemsTotal + dealFileGroupItem.files.length
      ), 0) : 0
  ), [dealFileGroups]);
  const scrollDeps: DepsItem[] = useMemo((): DepsItem[] => [
    dealFilesTotal, commonDealFilesTotal, isLoading, isEditMode,
  ], [dealFilesTotal, commonDealFilesTotal, isLoading, isEditMode]);

  const dealFileEditPermissions: string[] = useMemo((): string[] => [DEAL_FILE_EDIT], []);
  const dealFileListViewPermissions: string[] = useMemo((): string[] => [DEAL_FILE_LIST_VIEW], []);
  const dealFileCreatePermissions: string[] = useMemo((): string[] => [DEAL_FILE_CREATE], []);
  const dealFileGroupEditPermissions: string[] = useMemo((): string[] => [DEAL_FILE_GROUP_EDIT], []);
  const dealFileGroupDeletePermissions: string[] = useMemo((): string[] => [DEAL_FILE_GROUP_DELETE], []);

  const formPrefix: string = 'dealData.dealFilesData';
  const withButtons: boolean = isEditMode || isExportMode;
  const withDropBox: boolean = isEditMode;
  const isDealFileGroupEditAllowed: boolean = StructureService.checkPermission(DEAL_FILE_GROUP_EDIT, permissionList);
  const isGroupOrderAllowed: boolean = isCustomizeMode && expandedGroups && !expandedGroups.length
    && !isMobileDevice && !isSimple && isDealFileGroupEditAllowed;
  const dealFileGroupIds: number[] = dealFilesData && dealFilesData.length
    ? dealFilesData.map((dealFileGroupItem: DealFileGroup): number => dealFileGroupItem.id) : [];
  const columnsTotal: number = (isSimple ? COLUMNS_TOTAL_SIMPLE : COLUMNS_TOTAL)
    + (isEditMode ? COLUMNS_TOTAL_EDIT_MODE_OFFSET : 0);

  let commonRowIndex: number = 0;
  let isTableLoading: boolean = false;

  const addNewItem: Function = useCallback((dealFileGroupId: number): void => {
    if (onAddNewItems) {
      onAddNewItems(dealFileGroupId, [
        {
          id: StructureService.getNewItemId(dealFileNewItems[dealFileGroupId] || []),
          groupId: dealFileGroupId,
          name: '',
          file: '',
        },
      ]);
    }
  }, [dealFilesData, dealFileNewItems]);

  const addNewItems: Function = useCallback((dealFileGroup: DealFileGroup, fileList: File[]): void => {
    const newDealFileId: number = StructureService.getNewItemId(dealFileNewItems[dealFileGroup.id] || []);

    if (onAddNewItems) {
      onAddNewItems(dealFileGroup.id, fileList.map((fileItem: File, fileIndex: number): DealFileInput => ({
        id: newDealFileId + fileIndex,
        groupId: dealFileGroup.id,
        name: fileItem.name ? FormatService.formatCleanFileName(fileItem.name) : '',
        file: null,
      })));
    }

    if (onFilesChange) {
      onFilesChange(
        dealFileGroup.id,
        fileList.map((fileItem: File, fileIndex: number): number => newDealFileId + fileIndex),
        fileList,
      );
    }
  }, [dealFilesData, dealFileNewItems, fileDropDeps]);

  const longClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealFileGroupId: number, dealFileId: number,
  ): void => {
    if (!isSelectMode && !isExportMode && !isEditMode) {
      if (onLongClick) {
        onLongClick();
      }

      onSelectRow(event, dealFileGroupId, dealFileId, selectedRows, onSelectRows);
    }
  }, [selectedRows, isSelectMode, isExportMode, isEditMode]);

  const openModal: Function = useCallback((
    dealFileItem: DealFile, dealFileGroupItem: DealFileGroup, isNewItem?: boolean,
  ): void => {
    if (onOpenModal) {
      onOpenModal(dealFileItem, dealFileGroupItem, isNewItem);
    }
  }, []);

  const openActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(event.currentTarget);

    event.stopPropagation();
  }, []);

  const openGroupActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setGroupMenuAnchor(event.currentTarget);

    event.stopPropagation();
  }, []);

  const closeActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(null);

    event.stopPropagation();
  }, []);

  const closeGroupActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setGroupMenuAnchor(null);

    event.stopPropagation();
  }, []);

  const dealFileAnalyzeClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, file: File | null,
  ): void => {
    if (onDealFileAnalyzeClick && file) {
      onDealFileAnalyzeClick(file);
    }

    event.stopPropagation();
  }, [isDealFileAnalyzed]);

  const deleteClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealFileId: number, dealFileGroupId: number,
  ): void => {
    if (onDeleteClick) {
      onDeleteClick(dealFileId, dealFileGroupId);
    }

    event.stopPropagation();
  }, [dealFileNewItems]);

  const deleteGroupClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealFileGroupId: number,
  ): void => {
    if (onDeleteGroupClick) {
      onDeleteGroupClick(dealFileGroupId);
    }

    event.stopPropagation();
  }, [dealFileNewGroupItems]);

  const onRowClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealFile: DealFile, dealFileGroup: DealFileGroup,
    isListView?: boolean,
  ): void => {
    if (isExportMode || isSelectMode) {
      onSelectRow(event, dealFileGroup.id, dealFile.id, selectedRows, onSelectRows);
    } else if ((!isEditMode && !isNewDeal) || isListView) {
      const withNoSelection: boolean = ScreenService.checkNoSelection();

      if (withNoSelection) {
        openModal(dealFile, dealFileGroup);
      }
    }
  }, [isExportMode, isSelectMode, isEditMode, isNewDeal, selectedRows]);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setIsMessageError(false);
    setStatusMessage('');
  }, []);

  const onError: Function = useCallback((error: string): void => {
    setStatusMessage(error);
    setIsOpenMessage(true);
    setIsMessageError(true);
  }, []);

  const onGroupFieldClick: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    event.stopPropagation();
  }, []);

  const onCheckIsMenuItemVisible: Function = useCallback((
    actionMenuItem: ActionMenuItem, isAnalyzable: string | null,
  ): boolean => (
    ((actionMenuItem.isVisibleByEntityTypeValue && isAnalyzable && FormatService.extractBooleanValue(isAnalyzable))
      || !actionMenuItem.isVisibleByEntityTypeValue)
  ), []);

  const actionMenuItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <DeleteItemIcon />,
      text: 'Delete',
      permission: DEAL_FILE_DELETE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, dealFileId: number): void => {
        if (dealFileId) {
          onOpenDeleteConfirmModal(event, dealFileId);
        }
      },
    },
    {
      id: 2,
      icon: <ChatAssistantIcon />,
      text: 'Open File Chat',
      permission: DEAL_FILE_TEXT_DATA_VIEW,
      isVisibleByEntityTypeValue: FormatService.BOOLEAN_VALUE_TRUE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, dealFileId: number): void => {
        if (dealFileId) {
          const dealFileGroup: DealFileGroup = dealFilesData.find((dealFileGroupItem: DealFileGroup): boolean => (
            dealFileGroupItem.files && dealFileGroupItem.files.length
              ? !!dealFileGroupItem.files.find((dealFileItem: DealFile): boolean => dealFileItem.id === dealFileId)
              : false
          ));
          const dealFile: DealFile | null = dealFileGroup
            ? dealFileGroup.files.find((dealFileItem: DealFile): boolean => dealFileItem.id === dealFileId) || null
            : null;

          if (onOpenDealFileChatModal && dealFile) {
            onOpenDealFileChatModal(event, dealFile);
          }
        }
      },
    },
  ], [dealFilesData]);

  const actionGroupMenuItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <EditIcon />,
      text: 'Edit',
      permission: DEAL_FILE_GROUP_EDIT,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, dealFileGroupId: number): void => {
        if (dealFileGroupId) {
          onInlineGroupEditModeButtonClick(event, dealFileGroupId);
        }
      },
    },
    {
      id: 2,
      icon: <DeleteItemIcon />,
      text: 'Delete',
      permission: DEAL_FILE_GROUP_DELETE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, dealFileGroupId: number): void => {
        if (dealFileGroupId) {
          onOpenDeleteGroupConfirmModal(dealFileGroupId);
        }
      },
    },
  ], [isEditMode, dealFileGroupActiveIds]);

  const checkIsRowSelected: Function = useCallback((
    rowGroupId: number, rowId: number, selectedRows: StructureItem[],
  ): boolean => (
    selectedRows[rowGroupId] ? selectedRows[rowGroupId].includes(rowId) : false
  ), []);

  const onDropGroupItem: Function = useCallback((dragIndex: number, hoverIndex: number): void => {
    const newDealFileGroupIds: number[] = [...dealFileGroupIds];
    const activeDealFileGroupId: number = newDealFileGroupIds[dragIndex];
    StructureService.deleteListItemByIndex(newDealFileGroupIds, dragIndex);
    StructureService.insertListItemByIndex(newDealFileGroupIds, hoverIndex, activeDealFileGroupId);

    dispatch(saveDealFileGroupOrderSettings({
      dealId,
      dealFileGroupIds: newDealFileGroupIds.filter((dealFileGroupId: number): boolean => !!dealFileGroupId),
    }));
  }, [dealFileGroupIds, dealId]);

  const onHoverGroupItem: Function = useCallback((dragIndex: number, hoverIndex: number): void => {
    setActiveDealFileGroupIndex(dragIndex);
    setHoverDealFileGroupIndex(hoverIndex);
  }, []);

  const onReleaseGroupItem: Function = useCallback((): void => {
    setActiveDealFileGroupIndex(null);
    setHoverDealFileGroupIndex(null);
  }, []);

  const onScroll: MouseScrollHandlerFunction = useCallback((): void => {
    if (containerRef && containerRef.current) {
      ScreenService.infiniteScroll(
        isSmallScreen ? containerRef.current.offsetTop : containerRef.current.scrollTop,
        containerRef.current.clientHeight,
        itemListRef && itemListRef.current ? itemListRef.current.clientHeight : 0,
        (): void => {
          if (onLoadBatchData && dealFilesTotal > commonDealFilesTotal && !isLoading) {
            onLoadBatchData();
          }
        },
        isSmallScreen,
      );
    }
  }, [containerRef, itemListRef, isSmallScreen, dealFilesTotal, commonDealFilesTotal, isLoading]);

  useEffect((): void => {
    if (onExpansionGroupChange && dealFilesData && dealFileGroups && dealFilesData.length > dealFileGroups.length) {
      onExpansionGroupChange(null, dealFilesData.length);
    }

    setDealFileGroups(dealFilesData ? [...dealFilesData] : null);
  }, [dealFilesData]);

  useEffect((): void => {
    setDealFileGroups([...(dealFilesData || []), ...dealFileNewGroupItems]);
  }, [dealFileNewGroupItems]);

  useEffect((): void => {
    if (queryDealFileId && dealFileGroups && dealFileGroups.length) {
      dealFileGroups.forEach((dealFileGroup: DealFileGroup): void => {
        if (dealFileGroup.files && dealFileGroup.files.length) {
          dealFileGroup.files.forEach((dealFile: DealFile): void => {
            if (dealFile.file.link && dealFile.file.id === queryDealFileId) {
              ScreenService.downloadFile(dealFile.file.link);
            }
          });
        }
      });
    }
  }, [queryDealFileId]);

  useScroll(onScroll, scrollDeps);

  useArrowNavigation(inputRefs);

  return (
    <CheckAccess permissions={dealFileEditPermissions} isEnabled={isEditMode || isNewDeal}>
      {
        isEditMode && !isSmallScreen && (
          <PaddedFlexContainer>
            <FieldUpload
              isPlain
              isMultiple
              isDroppable
              isRecursive
              withDropOnly
              withDirectorySupport
              withAllFilesAllowed
              buttonLabel="Upload Folders"
              onDirectoriesChange={onFileGroupsChange}
              onError={onError}
              dropDeps={fileGroupDropDeps}
            />
          </PaddedFlexContainer>
        )
      }
      <Card>
        <ContentListContainerOffset
          withTabs
          isDefault={isSimple || isSmallScreen}
          withButtons={withButtons}
          withDropBox={withDropBox}
          ref={containerRef}
          onScroll={!isSmallScreen && !isSimple ? onScroll : undefined}
        >
          <div ref={itemListRef}>
            {
              dealFileGroups && !!dealFileGroups.length && (
                <Fragment>
                  {
                    SortService.sort(dealFileGroups, GROUP_SORT_STATE)
                      .map((dealFileGroup: DealFileGroup, dealFileGroupIndex: number): ReactElement | boolean => {
                        const isGroupCollapsed: boolean = expandedGroups
                          && !expandedGroups.includes(dealFileGroupIndex + 1);
                        const dealFileGroupFilesTotal: number = dealFileGroup && dealFileGroup.files
                          ? dealFileGroup.files.length : 0;
                        const dealFileGroupFilesSelectedTotal: number = selectedRows && selectedRows[dealFileGroup.id]
                          ? selectedRows[dealFileGroup.id].length : 0;
                        const isDealFileGroupSelected: boolean = withAllRowsSelected
                          || dealFileGroupFilesSelectedTotal === dealFileGroupFilesTotal;
                        const isDealFileGroupIndeterminate: boolean = !withAllRowsSelected
                          && dealFileGroupFilesSelectedTotal > 0
                          && dealFileGroupFilesSelectedTotal < dealFileGroupFilesTotal;
                        const formKeyDealFileGroup: string = FormService.getIndexedFormFieldKey(dealFileGroup.id);
                        const withGroupItems: boolean = dealFileGroup.files && !!dealFileGroup.files.length;
                        const isGroupLoading: boolean = !!dealFileGroup.filesTotal
                          && dealFileGroup.filesTotal > dealFileGroupFilesTotal;
                        const isNewItem: boolean = dealFileNewGroupItems && !!dealFileNewGroupItems.length
                          && !!dealFileNewGroupItems.find((newDealFileGroup: DealFileGroup): boolean => (
                            newDealFileGroup.id === dealFileGroup.id
                          ));
                        const isActiveGroup: boolean = dealFileGroupActiveIds
                          && dealFileGroupActiveIds.includes(dealFileGroup.id);
                        const isCurrentTableLoading: boolean = isTableLoading;
                        const isGroupRendered: boolean = ((!!dealFileGroup.filesTotal && !!dealFileGroupFilesTotal)
                          || !dealFileGroup.filesTotal) && !isCurrentTableLoading;
                        const isVisible: boolean = (!isExportMode && !isSelectMode) || ((isExportMode || isSelectMode)
                          && (withGroupItems || isGroupLoading));

                        isTableLoading = isTableLoading || isGroupLoading;

                        return (isGroupRendered || isGroupCollapsed) && (
                          <DraggableItem
                            withHighlight
                            isDisabled={!isGroupOrderAllowed}
                            isHovered={hoverDealFileGroupIndex === dealFileGroupIndex}
                            isHoverInverted={hoverDealFileGroupIndex && activeDealFileGroupIndex
                              && hoverDealFileGroupIndex > activeDealFileGroupIndex}
                            key={dealFileGroup.id}
                            itemId={dealFileGroup.id}
                            index={dealFileGroupIndex}
                            onDropItem={onDropGroupItem}
                            onHoverItem={onHoverGroupItem}
                            onReleaseItem={onReleaseGroupItem}
                          >
                            <ExpansionGroup
                              isTableGroup
                              isFixed={!!isSimple}
                              isExpanded={(isSmallScreen && !isGroupCollapsed) || !isSmallScreen}
                              withNoHeader={!isSmallScreen && !isSimple}
                              key={dealFileGroup.id}
                              onChange={!isSimple ? ((event: React.ChangeEvent): void => {
                                onExpansionGroupChange(event, dealFileGroupIndex + 1, !isGroupRendered);
                              }) : null}
                              headerComponent={(isSmallScreen || isSimple) && (
                                (!isEditMode && !isNewDeal) || !isActiveGroup ? (
                                  <FlexContainer isSpread>
                                    <FlexContainer isSpread isStart>
                                      {dealFileGroup.name}
                                    </FlexContainer>
                                    <ActionButtonContainer>
                                      {
                                        !isSimple && !isActiveGroup && (
                                          <CheckAccess permissions={dealFileGroupEditPermissions}>
                                            <ActionButton
                                              isLight
                                              isHidden={!!isSaving}
                                              withSmallPadding
                                              onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                onInlineGroupEditModeButtonClick(event, dealFileGroup.id);
                                              }}
                                            >
                                              <EditIcon />
                                            </ActionButton>
                                          </CheckAccess>
                                        )
                                      }
                                      {
                                        !isEditMode && !isNewDeal && !isExportMode && !isSelectMode
                                        && !isSmallScreen && (
                                          <CheckAccess permissions={dealFileGroupDeletePermissions}>
                                            <ActionButton
                                              isLight
                                              isActive
                                              withPopup
                                              withSmallPadding
                                              onClick={openGroupActionMenu}
                                              {...{
                                                [DATA_DEAL_FILE_GROUP_ID]: dealFileGroup.id,
                                              }}
                                            >
                                              <MoreIcon />
                                            </ActionButton>
                                          </CheckAccess>
                                        )
                                      }
                                    </ActionButtonContainer>
                                  </FlexContainer>
                                ) : (
                                  <FlexContainer isSpread>
                                    <CheckAccess permissions={dealFileGroupEditPermissions}>
                                      <FormFieldText
                                        isHidden
                                        name={`${formPrefix}[${formKeyDealFileGroup}].id`}
                                      />
                                      <FormFieldText
                                        isLight
                                        withUnderline
                                        name={`${formPrefix}[${formKeyDealFileGroup}].name`}
                                        placeholder="Group Name"
                                        onClick={onGroupFieldClick}
                                      />
                                    </CheckAccess>
                                    {
                                      isNewItem && (
                                        <div>
                                          <Tooltip title="Delete">
                                            <ActionButton
                                              isLight
                                              withSmallPadding
                                              onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                deleteGroupClick(event, dealFileGroup.id);
                                              }}
                                            >
                                              <RemoveIcon />
                                            </ActionButton>
                                          </Tooltip>
                                        </div>
                                      )
                                    }
                                  </FlexContainer>
                                )
                              )}
                            >
                              <CheckAccess permissions={dealFileListViewPermissions}>
                                {
                                  ((!isSmallScreen && !isNewDeal) || (!isMediumScreen && isNewDeal)) && (
                                    <HiddenBlock isActive={!isVisible}>
                                      <Table
                                        withStickyHeader
                                        isFullWidth={isEditMode || isNewDeal}
                                        ref={(ref: HTMLTableElement): void => {
                                          if (tableRefs.current) {
                                            tableRefs.current[dealFileGroup.id] = ref;
                                          }
                                        }}
                                      >
                                        {
                                          !isSmallScreen && !isSimple && (
                                            <TableHead isHeader>
                                              {
                                                (!isEditMode && !isNewDeal) || !isActiveGroup ? (
                                                  <TableRowGroupName
                                                    isButton
                                                    onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                      onExpansionGroupChange(
                                                        event,
                                                        dealFileGroupIndex + 1,
                                                        !isGroupRendered,
                                                      );
                                                    }}
                                                  >
                                                    {
                                                      (isExportMode || isSelectMode) && (
                                                        <TableCellCheckbox isSticky isStickyLeft isStickyGroupLeft>
                                                          {
                                                            dealFileGroup.files && !!dealFileGroup.files.length && (
                                                              <Checkbox
                                                                isHeader
                                                                isChecked={isDealFileGroupSelected}
                                                                isIndeterminate={isDealFileGroupIndeterminate}
                                                                id={`${ID_PREFIX_CHECKBOX_EXPORT_GROUP}-`
                                                                  + `${dealFileGroup.id}`}
                                                                onChange={(event: React.ChangeEvent): void => {
                                                                  onSelectAllGroupRows(
                                                                    event,
                                                                    dealFileGroup.id,
                                                                    selectedRows,
                                                                    onSelectRows,
                                                                    dealFilesData,
                                                                  );
                                                                }}
                                                              />
                                                            )
                                                          }
                                                        </TableCellCheckbox>
                                                      )
                                                    }
                                                    <TableCell
                                                      isSticky
                                                      isStickyLeft
                                                      isStickyGroupLeft
                                                      colSpan={COLUMNS_TOTAL_NAME}
                                                    >
                                                      {
                                                        isGroupOrderAllowed && (
                                                          <DragHandle />
                                                        )
                                                      }
                                                      {dealFileGroup.name}
                                                    </TableCell>
                                                    <TableCell colSpan={columnsTotal - COLUMNS_TOTAL_NAME - 1} />
                                                    <TableCell
                                                      isActions
                                                      isTextRight
                                                      isSticky
                                                      isStickyRight
                                                      isStickyGroupRight
                                                      width="7%"
                                                    >
                                                      <ActionButtonContainer>
                                                        {
                                                          (isEditMode || isNewDeal) && !isActiveGroup && (
                                                            <CheckAccess permissions={dealFileGroupEditPermissions}>
                                                              <ActionButton
                                                                isLight
                                                                withNoPadding
                                                                isHidden={!!isSaving}
                                                                onClick={(
                                                                  event: React.MouseEvent<Element, MouseEvent>,
                                                                ): void => {
                                                                  onInlineGroupEditModeButtonClick(
                                                                    event, dealFileGroup.id,
                                                                  );
                                                                }}
                                                              >
                                                                <EditIcon />
                                                              </ActionButton>
                                                            </CheckAccess>
                                                          )
                                                        }
                                                        {
                                                          !isExportMode && !isSelectMode && !isEditMode && (
                                                            <CheckAccess permissions={dealFileGroupDeletePermissions}>
                                                              <ActionButton
                                                                isLight
                                                                isActive
                                                                withPopup
                                                                withNoPadding
                                                                onClick={openGroupActionMenu}
                                                                {...{
                                                                  [DATA_DEAL_FILE_GROUP_ID]: dealFileGroup.id,
                                                                }}
                                                              >
                                                                <MoreIcon />
                                                              </ActionButton>
                                                            </CheckAccess>
                                                          )
                                                        }
                                                        <ActionButton
                                                          isLight
                                                          withNoPadding
                                                          isDisabled={!isGroupRendered}
                                                          onClick={(
                                                            event: React.MouseEvent<Element, MouseEvent>,
                                                          ): void => {
                                                            onExpansionGroupChange(
                                                              event,
                                                              dealFileGroupIndex + 1,
                                                              !isGroupRendered,
                                                            );
                                                          }}
                                                        >
                                                          {!isGroupCollapsed ? <CollapseIcon /> : <ExpandIcon />}
                                                        </ActionButton>
                                                      </ActionButtonContainer>
                                                    </TableCell>
                                                  </TableRowGroupName>
                                                ) : (
                                                  <TableRowGroupName
                                                    isButton
                                                    onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                      onExpansionGroupChange(
                                                        event,
                                                        dealFileGroupIndex + 1,
                                                        !isGroupRendered,
                                                      );
                                                    }}
                                                  >
                                                    <CheckAccess permissions={dealFileGroupEditPermissions}>
                                                      <TableCell>
                                                        <FormFieldText
                                                          isHidden
                                                          name={`${formPrefix}[${formKeyDealFileGroup}].id`}
                                                        />
                                                        <FormFieldText
                                                          isLight
                                                          withUnderline
                                                          name={`${formPrefix}[${formKeyDealFileGroup}].name`}
                                                          placeholder="Group Name"
                                                          onClick={onGroupFieldClick}
                                                        />
                                                      </TableCell>
                                                      <TableCell colSpan={columnsTotal - 2}>
                                                        <FieldUpload
                                                          isPlain
                                                          isLight
                                                          isNarrow
                                                          isMultiple
                                                          withDropOnly
                                                          withDirectorySupport
                                                          withAllFilesAllowed
                                                          isDroppable={!isSimple}
                                                          dropDeps={fileDropDeps}
                                                          buttonLabel="Upload New Files"
                                                          onFilesChange={(fileList: File[]): void => {
                                                            addNewItems(dealFileGroup, fileList);
                                                          }}
                                                          onError={onError}
                                                        />
                                                      </TableCell>
                                                      <TableCell isTextRight width="7%">
                                                        <ActionButtonContainer>
                                                          {
                                                            isNewItem && (
                                                              <Tooltip title="Delete">
                                                                <ActionButton
                                                                  isLight
                                                                  withNoPadding
                                                                  onClick={(
                                                                    event: React.MouseEvent<Element, MouseEvent>,
                                                                  ): void => {
                                                                    deleteGroupClick(event, dealFileGroup.id);
                                                                  }}
                                                                >
                                                                  <RemoveIcon />
                                                                </ActionButton>
                                                              </Tooltip>
                                                            )
                                                          }
                                                          <ActionButton
                                                            isLight
                                                            withNoPadding
                                                            isDisabled={!isGroupRendered}
                                                            onClick={(
                                                              event: React.MouseEvent<Element, MouseEvent>,
                                                            ): void => {
                                                              onExpansionGroupChange(
                                                                event,
                                                                dealFileGroupIndex + 1,
                                                                !isGroupRendered,
                                                              );
                                                            }}
                                                          >
                                                            {!isGroupCollapsed ? <CollapseIcon /> : <ExpandIcon />}
                                                          </ActionButton>
                                                        </ActionButtonContainer>
                                                      </TableCell>
                                                    </CheckAccess>
                                                  </TableRowGroupName>
                                                )
                                              }
                                            </TableHead>
                                          )
                                        }
                                        <TableBody
                                          isPlain
                                          withZebraRows
                                          isHidden={!isSmallScreen && isGroupCollapsed}
                                          withBorderedCells={!isCustomizeMode}
                                          withFixedCellsSizeExtended={(isEditMode || isNewDeal) && !isSimple}
                                        >
                                          {
                                            dealFileGroup.files && !!dealFileGroup.files.length
                                            && dealFileGroup.files.map((dealFile: DealFile): ReactElement => {
                                              const isSelected: boolean = selectedRows ? withAllRowsSelected
                                                || checkIsRowSelected(dealFileGroup.id, dealFile.id, selectedRows)
                                                : false;
                                              const isActive: boolean = dealFileActiveIds
                                                && dealFileActiveIds.includes(dealFile.id);
                                              const isInlineSaving: boolean = dealFileInlineSaveId !== null
                                              && dealFileGroupInlineSaveId !== null
                                                ? dealFile.id === dealFileInlineSaveId
                                                && dealFileGroup.id === dealFileGroupInlineSaveId : isSaving;
                                              const commonDealFileIndex: number = ++commonRowIndex;

                                              return (
                                                <DealFileRow
                                                  isActive={isActive}
                                                  isSaving={isSaving}
                                                  isSaveDisabled={isSaveDisabled}
                                                  isInlineSaving={isInlineSaving}
                                                  isSelected={isSelected}
                                                  isEditMode={isEditMode}
                                                  isExportMode={isExportMode}
                                                  isSelectMode={isSelectMode}
                                                  isCustomizeMode={isCustomizeMode}
                                                  isSimple={isSimple}
                                                  key={dealFile.id}
                                                  dealFileIndex={commonDealFileIndex}
                                                  dealFileItem={dealFile}
                                                  dealFileGroup={dealFileGroup}
                                                  dealFileFile={(dealFileFiles[dealFileGroup.id]
                                                    && dealFileFiles[dealFileGroup.id][dealFile.id])}
                                                  inputRefs={inputRefs}
                                                  actionMenuItems={actionMenuItems}
                                                  accessLevelList={accessLevelList}
                                                  fileDropDeps={fileDropDeps}
                                                  dealFileActiveIds={dealFileActiveIds}
                                                  onRowClick={onRowClick}
                                                  onLongClick={longClick}
                                                  onInlineSave={onInlineSave}
                                                  onInlineCancel={onInlineCancel}
                                                  onOpenModal={onOpenModal}
                                                  onFileChange={onFileChange}
                                                  onFileDelete={onFileDelete}
                                                  onFileViewClick={onFileViewClick}
                                                  onInlineEditModeButtonClick={onInlineEditModeButtonClick}
                                                  onOpenActionMenu={openActionMenu}
                                                />
                                              );
                                            })
                                          }
                                          {
                                            (!isEditMode && !isNewDeal)
                                            && (!dealFileGroup.files || !dealFileGroup.files.length) && (
                                              <TableRowNoContent>
                                                <TableCell colSpan={columnsTotal}>
                                                  No files in this group
                                                </TableCell>
                                              </TableRowNoContent>
                                            )
                                          }
                                          {
                                            (isEditMode || isNewDeal) && dealFileNewItems[dealFileGroup.id]
                                            && dealFileNewItems[dealFileGroup.id].map((
                                              dealFile: DealFile,
                                            ): ReactElement => {
                                              const commonDealFileIndex: number = ++commonRowIndex;

                                              return (
                                                <DealFileRowForm
                                                  isNewEntity
                                                  isNewDeal={isNewDeal}
                                                  isActive={!isNewDeal}
                                                  isSaving={isSaving}
                                                  isSaveDisabled={isSaveDisabled}
                                                  isSimple={isSimple}
                                                  isInlineSaving={(
                                                    dealFileInlineSaveId !== null && dealFileGroupInlineSaveId !== null
                                                      ? dealFile.id === dealFileInlineSaveId
                                                      && dealFileGroup.id === dealFileGroupInlineSaveId : isSaving
                                                  )}
                                                  isDealFileAnalyzed={isDealFileAnalyzed}
                                                  key={dealFile.id}
                                                  dealFileIndex={commonDealFileIndex}
                                                  dealFileData={dealFile}
                                                  dealFileGroupData={dealFileGroup}
                                                  dealFileFile={(dealFileFiles[dealFileGroup.id]
                                                    && dealFileFiles[dealFileGroup.id][dealFile.id])}
                                                  fileDropDeps={fileDropDeps}
                                                  inputRefs={inputRefs}
                                                  accessLevelList={accessLevelList}
                                                  onInlineSave={onInlineSave}
                                                  onInlineCancel={onInlineCancel}
                                                  onFileChange={onFileChange}
                                                  onFileDelete={onFileDelete}
                                                  onOpenModal={(): void => {
                                                    onOpenModal(dealFile, dealFileGroup, true);
                                                  }}
                                                  onDeleteClick={(
                                                    event: React.MouseEvent<Element, MouseEvent>,
                                                  ): void => {
                                                    deleteClick(event, dealFile.id, dealFileGroup.id);
                                                  }}
                                                  onDealFileAnalyzeClick={(
                                                    event: React.MouseEvent<Element, MouseEvent>,
                                                  ): void => {
                                                    dealFileAnalyzeClick(event, dealFileFiles[dealFileGroup.id]
                                                      && dealFileFiles[dealFileGroup.id][dealFile.id]);
                                                  }}
                                                />
                                              );
                                            })
                                          }
                                          {
                                            (isEditMode || isNewDeal) && (
                                              <CheckAccess permissions={dealFileCreatePermissions}>
                                                <TableRowAddNewItem
                                                  label="Add New File"
                                                  colSpan={columnsTotal}
                                                  onClick={(): void => {
                                                    addNewItem(dealFileGroup.id);
                                                  }}
                                                />
                                              </CheckAccess>
                                            )
                                          }
                                        </TableBody>
                                      </Table>
                                    </HiddenBlock>
                                  )
                                }
                                {
                                  ((isSmallScreen && !isNewDeal) || (isMediumScreen && isNewDeal)) && (
                                    <List>
                                      {
                                        dealFileGroup.files && !!dealFileGroup.files.length
                                        && dealFileGroup.files.map((dealFile: DealFile): ReactElement => {
                                          const isSelected: boolean = selectedRows ? withAllRowsSelected
                                            || checkIsRowSelected(dealFileGroup.id, dealFile.id, selectedRows) : false;

                                          return (
                                            <LongClickListItem
                                              isButton={!isSelectMode && !isExportMode}
                                              isExtended={!isSelectMode && !isExportMode}
                                              isActive={isSelected}
                                              key={dealFile.id}
                                              onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                onRowClick(event, dealFile, dealFileGroup, true);
                                              }}
                                              onLongClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                longClick(event, dealFileGroup.id, dealFile.id);
                                              }}
                                            >
                                              {
                                                (isExportMode || isSelectMode) && (
                                                  <ListItemCheckbox
                                                    isActive={isSelected}
                                                    itemId={dealFile.id}
                                                    onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                      onSelectRow(
                                                        event,
                                                        dealFileGroup.id,
                                                        dealFile.id,
                                                        selectedRows,
                                                        onSelectRows,
                                                      );
                                                    }}
                                                  />
                                                )
                                              }
                                              <ListItemText
                                                isBlock
                                                subText={!isEditMode && !isNewDeal && dealFile.file
                                                  ? dealFile.file.date : ''}
                                              >
                                                {dealFile.file && dealFile.file.name}
                                              </ListItemText>
                                              <ListItemText isBlock isBlockRight>
                                                {
                                                  !isEditMode && !isNewDeal ? (
                                                    !!dealFile.file && (
                                                      <FlexContainer isEnd>
                                                        <FilteredLink
                                                          isExternal
                                                          link={dealFile.file.link}
                                                          onMouseDown={(
                                                            event: React.MouseEvent<Element, MouseEvent>,
                                                          ): void => {
                                                            onFileViewClick(event, dealFile.file, dealFileGroup.name);
                                                          }}
                                                        >
                                                          <IconTextBlock icon={<DownloadIcon />} />
                                                        </FilteredLink>
                                                      </FlexContainer>
                                                    )
                                                  ) : (
                                                    <FlexContainer isEnd>
                                                      <Tooltip title="Open in modal">
                                                        <ActionButton
                                                          withSmallPadding
                                                          onClick={(
                                                            event: React.MouseEvent<Element, MouseEvent>,
                                                          ): void => {
                                                            onRowClick(event, dealFile, dealFileGroup, true);
                                                          }}
                                                        >
                                                          <ArrowLinkIcon />
                                                        </ActionButton>
                                                      </Tooltip>
                                                    </FlexContainer>
                                                  )
                                                }
                                              </ListItemText>
                                            </LongClickListItem>
                                          );
                                        })
                                      }
                                      {
                                        (!isEditMode && !isNewDeal) && (!dealFileGroup.files
                                          || !dealFileGroup.files.length) && (
                                          <NoContentBlock>
                                            No files in this group
                                          </NoContentBlock>
                                        )
                                      }
                                      {
                                        (isEditMode || isNewDeal) && dealFileNewItems[dealFileGroup.id]
                                        && dealFileNewItems[dealFileGroup.id].map((
                                          dealFile: DealFile,
                                        ): ReactElement => (
                                          <ListItem
                                            isButton
                                            isExtended
                                            isActive={!isNewDeal}
                                            key={dealFile.id}
                                            onClick={(): void => {
                                              openModal(dealFile, dealFileGroup, true);
                                            }}
                                          >
                                            <ListItemText isBlock>
                                              New File
                                            </ListItemText>
                                            <ListItemText isBlock isBlockRight>
                                              <FlexContainer isEnd>
                                                {
                                                  isNewDeal && !isDealFileAnalyzed && !!dealFileFiles[dealFileGroup.id]
                                                  && !!dealFileFiles[dealFileGroup.id][dealFile.id]
                                                  && FileService.checkFileExtension(
                                                    dealFileFiles[dealFileGroup.id][dealFile.id],
                                                    FILE_EXTENSION_LIST_ANALYZABLE,
                                                  ) && (
                                                    <Tooltip title="Analyze file content">
                                                      <ActionButton
                                                        isInline
                                                        withSmallPadding
                                                        onClick={onDealFileAnalyzeClick}
                                                      >
                                                        <AnalyzeIcon />
                                                      </ActionButton>
                                                    </Tooltip>
                                                  )
                                                }
                                                <Tooltip title="Delete">
                                                  <ActionButton
                                                    withSmallPadding
                                                    onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                      deleteClick(event, dealFile.id, dealFileGroup.id);
                                                    }}
                                                  >
                                                    <RemoveIcon />
                                                  </ActionButton>
                                                </Tooltip>
                                                <Tooltip title="Open in modal">
                                                  <ActionButton
                                                    withSmallPadding
                                                    onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                      onRowClick(event, dealFile, dealFileGroup, true);
                                                    }}
                                                  >
                                                    <ArrowLinkIcon />
                                                  </ActionButton>
                                                </Tooltip>
                                              </FlexContainer>
                                            </ListItemText>
                                          </ListItem>
                                        ))
                                      }
                                      {
                                        (isEditMode || isNewDeal) && (
                                          <CheckAccess permissions={dealFileCreatePermissions}>
                                            <Card
                                              isContainer
                                              isSelectable
                                              withSmallPadding
                                              onClick={(): void => {
                                                addNewItem(dealFileGroup.id);
                                              }}
                                            >
                                              <ActionButtonNewItem withNoHighlight label="Add New File" />
                                            </Card>
                                          </CheckAccess>
                                        )
                                      }
                                    </List>
                                  )
                                }
                              </CheckAccess>
                            </ExpansionGroup>
                          </DraggableItem>
                        );
                      })
                  }
                </Fragment>
              )
            }
            {
              dealFileGroups && !dealFileGroups.length && !isEditMode && !isNewDeal && (
                <NoContentBlock>
                  No deal files
                </NoContentBlock>
              )
            }
            {
              isLoading && (
                <Loader isInline />
              )
            }
          </div>
        </ContentListContainerOffset>
      </Card>
      <ContextMenu
        menuAnchor={groupMenuAnchor}
        actionMenuItems={actionGroupMenuItems}
        dataAttributeEntityId={DATA_DEAL_FILE_GROUP_ID}
        onClose={closeGroupActionMenu}
      />
      <ContextMenu
        menuAnchor={menuAnchor}
        actionMenuItems={actionMenuItems}
        dataAttributeEntityId={DATA_DEAL_FILE_ID}
        dataAttributeEntityType={DATA_IS_ANALYZABLE}
        onClose={closeActionMenu}
        onCheckIsMenuItemVisible={onCheckIsMenuItemVisible}
      />
      <StatusBar isOpen={isOpenMessage} isError={isMessageError} message={statusMessage} onClose={closeMessage} />
    </CheckAccess>
  );
});

export default DealFiles;
